import React, { useContext, useEffect, useRef, useState } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { useStaticQuery, graphql, Link } from 'gatsby';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { collectionNestedPaths, sanitizeString, slugify } from '../utils/helpers';
import ShopDropdown from '../components/Header/ShopDropdown';
import GetSearchResults from '../utils/get-search-results';
import { StoreContext } from '../context/store-context';

import WishlistSvg from '-!svg-react-loader!../assets/images/wishlist.svg';
import AccountSvg from '-!svg-react-loader!../assets/images/account.svg';
import CartSvg from '-!svg-react-loader!../assets/images/cart.svg';
import LocationSvg from '-!svg-react-loader!../assets/images/location.svg';
import SearchIcon from '-!svg-react-loader!../assets/images/search.svg';
import Dictation from '-!svg-react-loader!../assets/images/dictation.svg';
import ArrowSvg from '-!svg-react-loader!../assets/images/menu-arrow.svg';
import Arrow2Svg from '-!svg-react-loader!../assets/images/arrow.svg'
import Hmbr from '-!svg-react-loader!../assets/images/hmbr.svg';

const NavMenu = ({ items, name, isMobile = false }) => {
  if (isMobile && name === 'primary' && items[items.length - 1].ID !== 'lists') {
    items.push({
      ID: 'lists',
      title: 'Lists',
      url: '/wishlist/',
      classes: ['header__nav__item--lists'],
    });
  }

  const NavItem = ({ item, level = 1 }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const hasChildren = item.child_items && item.child_items.length > 0;
    const hasMegamenu = item.classes?.includes('has-dropdown--shop');
    let itemClasses = item.classes ? ' ' + item.classes.join(' ') : '';
    itemClasses += hasChildren || hasMegamenu ? ' has-children' : '';

    return (
      <li className={`header__nav__item${itemClasses}`}>
        <Link to={item.url} className="header__nav__item__link">
          {item.title}
          {level > 1 && (<ArrowSvg />)}
        </Link>

        {level === 1 && (hasChildren || hasMegamenu) && (
          <button
            className={`header__nav__item__toggle${dropdownOpen ? ' open' : ''}`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-label="Toggle Submenu"
          ><Arrow2Svg /></button>
        )}

        {hasChildren && (
          <ul className={`header__nav__item__submenu${dropdownOpen ? ' open' : ''}`}>
            {item.child_items.map(child => (
              <NavItem item={child} key={child.ID} level={level + 1} />
            ))}
          </ul>
        )}

        {hasMegamenu && (
          <ShopDropdown shopMenuOpen={dropdownOpen} setShopMenuOpen={setDropdownOpen} />
        )}
      </li>
    )
  };

  return (
    <nav className={`header__nav header__nav--${name}`}>
      <ul className="header__nav__menu">
        {items.map(item => (
          <NavItem item={item} key={item.ID} />
        ))}
      </ul>
    </nav>
  );
}


// const HeaderNotification = ({ isMobile }) => {
//   const copyButtonRef = useRef(null);

//   return (
//     <div className="header__notification">
//       <strong>Save 8.75%</strong> on <Link to="/collection/decking-and-railing/composite-and-pvc-decking/trunorth-composite-decking/">TruNorth</Link> {isMobile ? '&' : 'and'} <Link to="/collection/decking-and-railing/regal-aluminum-railing/">Regal</Link>{isMobile ? '!' : ' - Decking or Railing Purchase'}
//       <button
//         ref={copyButtonRef}
//         className="copy-code-btn"
//         onClick={() => {
//           if (typeof window !== 'undefined') {
//             navigator.clipboard.writeText('Spring25');
//             copyButtonRef.current.classList.add('is-copied');
//             setTimeout(() => {
//               copyButtonRef.current.classList.remove('is-copied');
//             }, 3000);
//           }
//         }}
//       >Use code: Spring25</button>
//     </div>
//   )
// }


const HeaderSearch = ({ products, collections, pages, posts, search }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState();
  const searchEl = useRef(null);
  // const popular = search.popular.split(/(?:\r\n|\r|\n)/);
  const autocomplete = search.autocomplete.split(/(?:\r\n|\r|\n)/);
  products = products?.map(product => ({
    ...product,
    sku: product.variants?.map(variant => variant.sku).filter(Boolean) || [],
  }));

  // Handle speech recognition
  const {
    transcript,
    listening,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();
  const handleListing = () => {
    if(!browserSupportsSpeechRecognition) {
      window.alert("Sorry, your browser doesn't support speech recognition.");
    }

    if (!listening) {
      setSearchTerm("");
      SpeechRecognition.startListening();
    } else {
      SpeechRecognition.stopListening();
    }
  };

  // Set the search term from the speech recognition transcript
  useEffect(() => {
    setSearchTerm(sanitizeString(transcript?.toLowerCase()))
  }, [transcript])

  useEffect(() => {
    // Prevent body scroll when search results are open
    if(searchTerm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    const allResults = {
      products: GetSearchResults(products, 'product', searchTerm, true),
      collections: GetSearchResults(collections, null, searchTerm, true),
      pages: GetSearchResults(pages, 'post', searchTerm, true),
      posts: GetSearchResults(posts, 'post', searchTerm, true),
      autocomplete: GetSearchResults(autocomplete, 'string', searchTerm, true),
    };

    const topResults = Object.entries(allResults)
      .flatMap(([type, groups]) =>
        groups.flatMap(item => {
          if (type === 'collections') item.score = (item.score || 0) + 5;
          if (type === 'autocomplete') item.score = (item.score || 0) + 15;
          item.type = type;
          return item;
        })
      )
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map(({ score, ...rest }) => rest);

    setSearchResults(topResults);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [searchTerm]);

  // Open search when there is a search term
  useEffect(() => {
    if(searchTerm) {
      setSearchIsOpen(true);
    } else {
      setSearchIsOpen(false);
    }
  }, [searchTerm])

  // Close search when clicking outside the search wrapper
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        searchEl.current &&
        !searchEl.current.contains(event.target) &&
        searchTerm
      ) {
        setSearchIsOpen(false);
      }
    }
    document.addEventListener('mouseup', handleClickOutside);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [searchTerm]);

  return (
    <div className="header__search" ref={searchEl}>
      <button
        className={`header__search__dictation${listening ? ' listening' : ''}`}
        onClick={handleListing}
        aria-label={`Click to ${listening ? 'stop' : 'start'} dictation`}
      ><Dictation /></button>

      <form
        className="header__search__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (searchTerm) {
            window.location.href = `/search-refresh?s=${encodeURIComponent(searchTerm)}`;
          }
        }}
      >
        <label htmlFor="search" className="VisuallyHidden">Search</label>
        <input
          type="search"
          className="header__search__input"
          placeholder={listening ? 'Listening...' : 'What can we help you with today?'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setSearchIsOpen(true)}
        />
        <button
          type="submit"
          className="header__search__submit"
          aria-label="Submit"
        ><SearchIcon /></button>
      </form>

      {searchTerm && searchIsOpen && searchResults && (
        // Header Search Results
        <ul className="hsr">
          {searchResults.length > 0 ? searchResults.map((result, idx) => {
            const title = result.obj?.title || result.title || "";
            const defaultLink = `/search-refresh?s=${encodeURIComponent(title)}`;
            let link = defaultLink;
            switch (result.type) {
              case "products":
                link = result.obj?.handle ? `/product/${result.obj.handle}` : defaultLink;
                break;
              case "collections":
                link = result.obj?.handle ? `/collection/${result.obj.handle}` : defaultLink;
                break;
              case "pages":
              case "posts":
                link = result.obj?.slug ? `/${result.obj.slug}` : defaultLink;
                break;
              default:
                break;
            }

            // Highlight matching part of title
            let displayTitle = title;
            if (searchTerm) {
              const regex = new RegExp(`(${searchTerm})`, 'ig');
              displayTitle = title.replace(regex, '<strong>$1</strong>');
            }

            return (
              <li className="hsr__result" key={idx}>
                <Link to={link}>
                  <span dangerouslySetInnerHTML={{ __html: displayTitle }} />
                </Link>
              </li>
            );
          }) : (
            <li className="hsr__result hsr__result--empty">No results found.</li>
          )}
        </ul>
      )}
    </div>
  )
}


const Header = ({ isMobile }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerElem = useRef(null);
  const headerTopElem = useRef(null);
  const headerActionsElem = useRef(null);
  const { toggleCartIsOpen, cart } = useContext(StoreContext);

  const headerActions = [
    {
      title: 'Lists',
      url: '/wishlist',
      icon: <WishlistSvg />,
    },
    {
      title: 'Account',
      url: '/account',
      icon: <AccountSvg />,
    },
  ];

  const headerQuery = useStaticQuery(graphql`
    query {
      wp {
        themeOptions {
          acfThemeOptions {
            header {
              logo {
                localFile {
                  childCloudinaryAsset {
                    gatsbyImageData
                  }
                }
              }
            }
            search {
              autocomplete
              popular
            }
          }
        }
      }
      primaryNav: wordpressMenuLocation(slug: {eq: "primary_navigation"}) {
        menuData {
          items {
            ID
            title
            url
            classes
            child_items {
              ID
              title
              url
            }
          }
        }
      }
      topMenu: wordpressMenuLocation(slug: {eq: "top_menu"}) {
        menuData {
          items {
            ID
            title
            url
          }
        }
      }
      posts: allWpPost(filter: {status: {eq: "publish"}}) {
        nodes {
          title
          slug
        }
      }
      pages: allWpPage(filter: {status: {eq: "publish"}}) {
        nodes {
          title
          slug
        }
      }
      products: allShopifyProduct(filter: {priceRangeV2: {maxVariantPrice: {amount: {gt: 0.01}}, minVariantPrice: {amount: {gt: 0.01}}}, status: {eq: ACTIVE}}) {
        nodes {
          title
          handle
          tags
          variants {
            sku
          }
        }
      }
      collections: allShopifyCollection {
        nodes {
          id
          title
          handle
          subCollections: metafield(key: "collection_subs", namespace: "collection") {
            value
          }
        }
      }
    }
  `)
  const primaryNavItems = headerQuery.primaryNav?.menuData.items;
  const topMenuItems = headerQuery.topMenu?.menuData.items;

  // Set --header-height CSS variable
  useEffect(() => {
    if (!headerElem.current || !headerTopElem.current || !headerActionsElem.current) return;

    const headerHeight = () => {
      const headerHeight = headerElem.current.offsetHeight;
      const headerTopHeight = headerTopElem.current.offsetHeight;
      const headerActionsHeight = headerActionsElem.current.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      document.documentElement.style.setProperty('--header-top-height', `${headerTopHeight}px`);
      document.documentElement.style.setProperty('--header-actions-height', `${headerActionsHeight}px`);
    };

    setTimeout(() => {headerHeight()}, 1);
    window.addEventListener('resize', headerHeight);
    return () => window.removeEventListener('resize', headerHeight);
  }, [headerElem]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [mobileMenuOpen]);

  // Add classes to header on scroll
  useEffect(() => {
    const header = headerElem.current;
    let lastScrollTop = window.scrollY;
    let ticking = false;
    let headerHeight = header.offsetHeight;

    const updateHeaderHeight = () => {
      headerHeight = header.offsetHeight;
    };

    const onScroll = () => {
      const scrollTop = window.scrollY;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          // direction
          if (scrollTop > lastScrollTop) {
            header.classList.add('scrolldown');
            header.classList.remove('scrollup');
          } else {
            header.classList.add('scrollup');
            header.classList.remove('scrolldown');
          }

          // scrolltop
          if (scrollTop < headerHeight) {
            header.classList.add('scrolltop');
          } else {
            header.classList.remove('scrolltop');
          }

          // scrolled
          if (scrollTop > 10) {
            header.classList.add('scrolled');
          } else {
            header.classList.remove('scrolled');
          }

          lastScrollTop = scrollTop;
          ticking = false;

          requestAnimationFrame(updateHeaderHeight);
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', updateHeaderHeight);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  return (<>
    <a href="#main-content" className="sr-only">Skip to main content</a>
    <div className="header__top" ref={headerTopElem}>
      <div className="header__top__inner Container">
      <Link to="/contact/" className="header__top__location" onClick={() => {
          console.log('Link click event triggered');

          // GTM check
          if (typeof window !== 'undefined') {
            // GTM ID check
            if (window.google_tag_manager && window.google_tag_manager['GTM-P9CDNPR6']) {
              console.log('GTM is loaded and working');

              // dataLayer check
              if (window.dataLayer) {
                window.dataLayer.push({
                  event: 'conversion',
                  conversionId: '743066783',
                  conversionLabel: 'qLCfCKm11Z0aEJ-ZqeIC'
                });
                console.log('dataLayer event sent');
              }

              // gtag check
              if (typeof window.gtag === 'function') {
                window.gtag('event', 'conversion', {
                  send_to: 'AW-743066783/qLCfCKm11Z0aEJ-ZqeIC'
                });
                console.log('gtag event sent');
              }
            } else {
              console.warn('GTM is not loaded or GTM-P9CDNTR6 not found');
            }
          }
        }}>
          <LocationSvg /> Our Locations & Hours
        </Link>

        {/* <HeaderNotification isMobile={isMobile} /> */}
        <div></div>

        <NavMenu items={topMenuItems} name="top" />
      </div>
    </div>

    <header className="header" ref={headerElem}>
      <div className="header__inner Container">

        <Link to="/" className="header__logo">
          <StaticImage
            src="../assets/images/logo.png"
            className="header__logo__image"
            alt="LENCO Lumber"
            width={234}
            aspectRatio={234/81}
            quality={100}
            loading="eager"
          />
          <StaticImage
            src="../assets/images/scroll-logo.png"
            class="header__logo__image header__logo__image--scroll"
            alt="LENCO Lumber"
            width={111}
            aspectRatio={111/109}
            quality={100}
            loading="eager"
          />
        </Link>

        {(!isMobile || mobileMenuOpen) && (
          <div className="header__navs" style={{display: mobileMenuOpen ? 'block' : null}}>
            <NavMenu items={primaryNavItems} name="primary" isMobile={isMobile} />
            {isMobile && (
              <a href="tel:+18002584585" className="header__phone">
                Questions? <strong>(800) 258-4585</strong>
              </a>
            )}
          </div>
        )}

        <HeaderSearch
          products={headerQuery.products.nodes}
          collections={collectionNestedPaths(headerQuery.collections.nodes)}
          posts={headerQuery.posts.nodes}
          pages={headerQuery.pages.nodes}
          search={headerQuery.wp.themeOptions.acfThemeOptions.search}
        />

        <ul className="header__actions" ref={headerActionsElem}>
          {(!isMobile || !mobileMenuOpen) && (<>
            {headerActions.map(action => (
              <li
                className={`header__action header__action--${slugify(action.title)}`}
                key={action.title}
              >
                <Link
                  to={action.url}
                  className="header__action__link"
                  onClick={action.action}
                  onKeyDown={action.action}
                >
                  {action.icon}
                  <span className="header__action__label">{action.title}</span>
                </Link>
              </li>
            ))}

            <li className={`header__action header__action--cart${cart?.lines?.nodes.some(i => i.quantity > 0) ? ' cart-not-empty' : ''}`}>
              <button
                className="header__action__link"
                onClick={toggleCartIsOpen}
                onKeyDown={toggleCartIsOpen}
              >
                <CartSvg />
                <span className="header__action__label">Cart</span>
              </button>
            </li>
          </>)}

          <li className="header__action header__action--navtoggle">
            <button
              className={`header__action__link Button--hmbr${mobileMenuOpen ? ' open' : ''}`}
              onClick={() => { setMobileMenuOpen(!mobileMenuOpen) }}
              onKeyDown={() => { setMobileMenuOpen(!mobileMenuOpen) }}
            >
              <Hmbr />
              <span className="header__action__label">Toggle Navigation</span>
            </button>
          </li>
        </ul>

      </div>
    </header>
    <div className="header__overlay"></div>
  </>)
}

export default Header
